/**
 * Hex grid math utilities — shared between server and client.
 * DO NOT EDIT in server/src/shared/common/ or phaser/src/shared/common/
 * Edit the canonical copy in shared/ and run: node scripts/sync-shared.js
 */

import type { HexCoord } from './HexCoord.js';
import { HEX_DIRECTIONS } from './HexCoord.js';

export function hexDistance(a: HexCoord, b: HexCoord): number {
  return Math.max(Math.abs(a.q - b.q), Math.abs(a.r - b.r), Math.abs(a.q + a.r - b.q - b.r));
}

export function hexNeighbors(hex: HexCoord): HexCoord[] {
  return HEX_DIRECTIONS.map((dir) => ({
    q: hex.q + dir.q,
    r: hex.r + dir.r,
  }));
}

export function hexEquals(a: HexCoord, b: HexCoord): boolean {
  return a.q === b.q && a.r === b.r;
}

export function hexKey(hex: HexCoord): string {
  return `${hex.q},${hex.r}`;
}

export function parseHexKey(key: string): HexCoord {
  const [q, r] = key.split(',').map(Number);
  return { q, r };
}

export function hexesInRange(center: HexCoord, range: number): HexCoord[] {
  const results: HexCoord[] = [];
  for (let q = -range; q <= range; q++) {
    for (let r = Math.max(-range, -q - range); r <= Math.min(range, -q + range); r++) {
      results.push({ q: center.q + q, r: center.r + r });
    }
  }
  return results;
}

// ── Multi-hex footprint utilities ────────────────────────────

/** Footprint size type: 1 (single hex), 2 (vertical pair), 3 (center + 2 adj), 7 (center + 6 neighbors) */
export type FootprintSize = 1 | 2 | 3 | 7;

/** Station type to footprint size mapping */
export const STATION_FOOTPRINT: Record<string, FootprintSize> = {
  waypoint: 1,
  depot: 1,
  fortress: 2,
  capital: 7,
};

/** Get footprint size for a planet */
export function getPlanetFootprint(isHomePlanet: boolean): FootprintSize {
  return isHomePlanet ? 7 : 3;
}

/**
 * Get all hexes occupied by a multi-hex object.
 * - size 1: just the center
 * - size 2: center + neighbor below (direction 5: {q:0, r:+1}) — vertical pair
 * - size 3: center + 2 neighbors (directions 0 and 1 — upper-right cluster)
 * - size 7: center + all 6 neighbors (full hex flower)
 */
export function getFootprintHexes(center: HexCoord, size: FootprintSize): HexCoord[] {
  if (size === 1) return [center];
  if (size === 7) return hexesInRange(center, 1);
  if (size === 2) {
    return [
      center,
      { q: center.q + HEX_DIRECTIONS[5].q, r: center.r + HEX_DIRECTIONS[5].r },
    ];
  }
  // size === 3: center + directions 0 and 1
  return [
    center,
    { q: center.q + HEX_DIRECTIONS[0].q, r: center.r + HEX_DIRECTIONS[0].r },
    { q: center.q + HEX_DIRECTIONS[1].q, r: center.r + HEX_DIRECTIONS[1].r },
  ];
}

/**
 * Compute the minimum hex distance from a point to any hex in a footprint.
 * Used for range checks (attack, dock, deliver, raid, defense).
 */
export function distanceToFootprint(point: HexCoord, anchor: HexCoord, size: FootprintSize): number {
  const footprint = getFootprintHexes(anchor, size);
  let min = Infinity;
  for (const hex of footprint) {
    const d = hexDistance(point, hex);
    if (d < min) min = d;
    if (d === 0) return 0; // early exit
  }
  return min;
}

/**
 * Build a Set of hex keys for all hexes occupied by a footprint.
 */
export function getFootprintKeySet(anchor: HexCoord, size: FootprintSize): Set<string> {
  const hexes = getFootprintHexes(anchor, size);
  const keys = new Set<string>();
  for (const h of hexes) keys.add(hexKey(h));
  return keys;
}
